import { useState } from 'react'
import { AlertTriangle, Clock, Plus, RefreshCw, Save, Trash2, Users, X } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  IconButton,
  Input,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  useCancelUserGroupReduction,
  useContractUserGroups,
  useCreateContractUserGroup,
  useDeleteContractUserGroup,
  usePendingUserGroupReductions,
  useSyncContractUserGroups,
  useUpdateContractUserGroup,
} from '../api/contractsApi.ts'
import type { ContractUserGroup, PendingUserGroupReduction } from '../types.ts'

/**
 * User-Klassen pro Vertrag (Etappe 14). Wird im Positionen-Tab ueber der Positionsliste
 * angezeigt; jede Position kann spaeter eine Klasse referenzieren.
 * §4.3: Reduktionen unter 80% werden abgelehnt; alle Reduktionen haben eine 30-Tage-Frist.
 */

export function UserGroupsPanel({ contractId }: { contractId: string }) {
  const { toast } = useToast()
  const { data = [], isLoading } = useContractUserGroups(contractId)
  const { data: pendingReductions = [] } = usePendingUserGroupReductions(contractId)
  const create = useCreateContractUserGroup(contractId)
  const update = useUpdateContractUserGroup(contractId)
  const del = useDeleteContractUserGroup(contractId)
  const cancelReduction = useCancelUserGroupReduction(contractId)
  const sync = useSyncContractUserGroups(contractId)

  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [count, setCount] = useState('0')
  const [confirmDelete, setConfirmDelete] = useState<ContractUserGroup | null>(null)

  function reset() {
    setName('')
    setDescription('')
    setCount('0')
    setAdding(false)
  }

  function handleCreate() {
    if (!name.trim()) {
      toast({ title: 'Name fehlt', tone: 'danger' })
      return
    }
    create.mutate(
      {
        name: name.trim(),
        description: description || null,
        count: Number(count) || 0,
      },
      {
        onSuccess: () => {
          toast({ title: 'User-Klasse angelegt', tone: 'success' })
          reset()
        },
        onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
      },
    )
  }

  // Map groupId → pendingReduction fuer schnellen Lookup im Row
  const pendingByGroup = new Map<string, PendingUserGroupReduction>(
    pendingReductions.map((r) => [r.contractUserGroupId, r]),
  )

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <h3 className="text-h4 font-semibold text-gray-900">User-Klassen</h3>
          </div>
          {!adding && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="xs"
                leadingIcon={<RefreshCw className="w-3 h-3" />}
                loading={sync.isPending}
                onClick={() =>
                  sync.mutate(undefined, {
                    onSuccess: (r) => {
                      const msg = `${r.groupsSynced} Klassen synchronisiert${r.groupsSkipped > 0 ? ` · ${r.groupsSkipped} ohne TANSS-Filter übersprungen` : ''}${r.errors.length > 0 ? ` · ${r.errors.length} Fehler` : ''}`
                      toast({ title: 'TANSS-Sync', description: msg, tone: r.errors.length > 0 ? 'danger' : 'success' })
                    },
                    onError: (err) => toast({ title: 'Sync fehlgeschlagen', description: err.message, tone: 'danger' }),
                  })
                }
              >
                TANSS-Sync
              </Button>
              <Button variant="ghost" size="xs" leadingIcon={<Plus className="w-3 h-3" />} onClick={() => setAdding(true)}>
                Neue Klasse
              </Button>
            </div>
          )}
        </div>
        <p className="text-caption text-gray-500 mt-1">
          Klassen wie „Voll-User", „Teilzeit-User" — referenziert von Positionen mit Mengen-Modus
          „pro User-Klasse" zur automatischen Skalierung.
        </p>
      </Card.Header>
      <Card.Body className="space-y-2">
        {adding && (
          <div className="grid grid-cols-[1fr_2fr_120px_auto] gap-2 items-end p-2 rounded border border-gray-200 bg-gray-50">
            <Input label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
            <Input label="Beschreibung" value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
            <Input label="Anzahl" type="number" value={count} onChange={(e) => setCount(e.currentTarget.value)} />
            <div className="flex gap-1">
              <Button variant="primary" size="sm" onClick={handleCreate} loading={create.isPending}>
                Anlegen
              </Button>
              <Button variant="ghost" size="sm" onClick={reset}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-small text-gray-500">Lade …</p>
        ) : data.length === 0 ? (
          <p className="text-small text-gray-500">
            Noch keine User-Klassen. Lege z. B. „Voll-User" oder „Teilzeit-User" an.
          </p>
        ) : (
          <ul className="space-y-1">
            {data.map((g) => (
              <UserGroupRow
                key={g.id}
                group={g}
                pendingReduction={pendingByGroup.get(g.id)}
                onUpdate={(patch) =>
                  update.mutate(
                    { id: g.id, patch },
                    {
                      onSuccess: (result) => {
                        if (result.pendingReductionCreated && result.effectiveAt) {
                          const date = new Date(result.effectiveAt).toLocaleDateString('de-DE')
                          toast({
                            title: 'Reduktion vorgemerkt',
                            description: `Die Anzahl wird zum ${date} für die Abrechnung wirksam (30-Tage-Frist gemäß §4.3).`,
                            tone: 'info',
                          })
                        }
                      },
                      onError: (err) =>
                        toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
                    },
                  )
                }
                onDelete={() => setConfirmDelete(g)}
                onCancelReduction={(reductionId) =>
                  cancelReduction.mutate(reductionId, {
                    onSuccess: () => toast({ title: 'Reduktion storniert', tone: 'success' }),
                    onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
                  })
                }
              />
            ))}
          </ul>
        )}
      </Card.Body>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="User-Klasse löschen?"
        message={confirmDelete ? `„${confirmDelete.name}" wird gelöscht.` : ''}
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) => {
              toast({ title: 'Löschen nicht möglich', description: err.message, tone: 'danger' })
              setConfirmDelete(null)
            },
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Card>
  )
}

function UserGroupRow({
  group,
  pendingReduction,
  onUpdate,
  onDelete,
  onCancelReduction,
}: {
  group: ContractUserGroup
  pendingReduction?: PendingUserGroupReduction
  onUpdate: (patch: { name?: string; description?: string | null; count?: number }) => void
  onDelete: () => void
  onCancelReduction: (reductionId: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description ?? '')
  const [count, setCount] = useState(String(group.count))

  const minAllowed = group.minQuantityPercent != null
    ? Math.ceil(group.count * (group.minQuantityPercent / 100))
    : null

  const newCountNum = Number(count)
  const isReduction = newCountNum < group.count
  const isBelow80 = minAllowed != null && newCountNum < minAllowed

  if (!editing) {
    return (
      <li className="space-y-1">
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50">
          <div className="flex items-center gap-3 flex-1">
            <Badge variant="info" size="sm">
              {group.count} User
            </Badge>
            <span className="text-small font-medium text-gray-900">{group.name}</span>
            {group.description && <span className="text-caption text-gray-500">{group.description}</span>}
            {group.minQuantityPercent != null && (
              <Badge variant="warning" size="sm">
                Min. {group.minQuantityPercent}%
              </Badge>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="xs" onClick={() => setEditing(true)}>
              Bearbeiten
            </Button>
            <IconButton
              icon={<Trash2 className="w-3 h-3" />}
              aria-label="Löschen"
              variant="ghost"
              size="xs"
              onClick={onDelete}
            />
          </div>
        </div>
        {pendingReduction && (
          <div className="mx-2 mb-1 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-small text-amber-800">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <span className="font-medium">Reduktion ausstehend:</span>{' '}
              {pendingReduction.oldCount} → {pendingReduction.newCount} User.
              Für Abrechnung wirksam ab{' '}
              <span className="font-medium">
                {new Date(pendingReduction.effectiveAt).toLocaleDateString('de-DE')}
              </span>{' '}
              (30-Tage-Frist §4.3).
            </div>
            <IconButton
              icon={<X className="w-3 h-3" />}
              aria-label="Reduktion stornieren"
              variant="ghost"
              size="xs"
              onClick={() => onCancelReduction(pendingReduction.id)}
            />
          </div>
        )}
      </li>
    )
  }

  return (
    <li className="space-y-1">
      <div className="grid grid-cols-[1fr_2fr_120px_auto] gap-2 items-end p-2 rounded border border-gray-200 bg-gray-50">
        <Input label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <Textarea label="Beschreibung" rows={1} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
        <Input label="Anzahl" type="number" value={count} onChange={(e) => setCount(e.currentTarget.value)} />
        <div className="flex gap-1">
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Save className="w-3 h-3" />}
            disabled={isBelow80}
            onClick={() => {
              onUpdate({ name, description: description || null, count: Number(count) || 0 })
              setEditing(false)
            }}
          >
            Speichern
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setCount(String(group.count)) }}>
            Abbrechen
          </Button>
        </div>
      </div>
      {editing && isReduction && (
        <div className={`mx-2 flex items-start gap-2 rounded border px-3 py-2 text-small ${isBelow80 ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            {isBelow80 ? (
              <>
                <span className="font-medium">Reduktion abgelehnt:</span>{' '}
                Mindestmenge {minAllowed} User ({group.minQuantityPercent}% von {group.count}).
                Bitte Anzahl auf mindestens {minAllowed} erhöhen.
              </>
            ) : (
              <>
                <span className="font-medium">Reduktion mit 30-Tage-Frist:</span>{' '}
                Die Anzahl wird sofort gespeichert, aber für die Abrechnung erst nach 30 Tagen wirksam (§4.3).
                Bis dahin wird die bisherige Anzahl ({group.count}) abgerechnet.
              </>
            )}
          </div>
        </div>
      )}
    </li>
  )
}
